import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  Moon,
  Sun,
  Search,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/context/ThemeContext";
import ProjectSelector from "@/components/crm/ProjectSelector";
import NotificationsDropdown from "@/components/crm/NotificationsDropdown";

export default function Topbar({
  onOpenMobileMenu,
  onAddLead,
  onRefresh,
  search = "",
  onSearchChange,
  showSearch = true,
  showAdd = true,
  title,
  subtitle,
  rightExtra,
}) {
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // When the user types in the topbar search, take them to /leads with the
  // query — UNLESS they are already on /leads (where the search is local).
  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (location.pathname === "/leads") return;
    const q = (search || "").trim();
    navigate(q ? `/leads?q=${encodeURIComponent(q)}` : "/leads");
  };

  // Escape clears the search box (any page) so the user doesn't have to
  // manually backspace. Same standard as Spotlight, VS Code, Chrome, etc.
  const onSearchKeyDown = (e) => {
    if (e.key === "Escape" && search) {
      e.preventDefault();
      onSearchChange?.("");
    }
  };

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur-lg"
    >
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        {/* Mobile menu */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenMobileMenu}
          data-testid="topbar-menu-btn"
          className="lg:hidden touch-target"
          aria-label="Menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Title (mobile + desktop fallback) */}
        {title && (
          <div className="min-w-0 flex-shrink-0 lg:hidden">
            <h1 className="font-heading truncate text-base font-semibold leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Search */}
        {showSearch && (
          <form onSubmit={onSubmitSearch} className="hidden md:flex flex-1 max-w-md" role="search">
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Rechercher (nom, téléphone, ville, programme…)"
                data-testid="topbar-search"
                className="h-10 pl-9 pr-12 bg-muted/40 border-transparent focus-visible:bg-background"
                aria-label="Rechercher un lead"
              />
              <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground sm:inline-block">
                Entrée
              </kbd>
            </div>
          </form>
        )}

        {/* Spacer for desktop when no search */}
        {!showSearch && <div className="hidden md:block flex-1" />}

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-1.5">
          {/* Project selector — desktop only */}
          <div className="hidden xl:block">
            <ProjectSelector />
          </div>

          {rightExtra}

          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              data-testid="topbar-refresh-btn"
              className="touch-target hidden sm:inline-flex"
              title="Rafraîchir"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}

          <NotificationsDropdown />

          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="topbar-theme-btn"
            className="touch-target"
            title={theme === "dark" ? "Mode clair" : "Mode sombre"}
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {showAdd && onAddLead && (
            <Button
              onClick={onAddLead}
              data-testid="topbar-add-lead-btn"
              className="ml-1 gap-1.5 bg-accent-navy text-white shadow-soft hover:bg-accent-navy/90 hover:shadow-elevated dark:bg-primary dark:text-primary-foreground"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nouveau lead</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mobile-only second row: project selector + search */}
      <div className="flex items-center gap-2 border-t border-border/40 px-4 py-2 md:hidden">
        <ProjectSelector />
        {showSearch && (
          <form onSubmit={onSubmitSearch} className="relative flex-1" role="search">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder="Rechercher un lead…"
              data-testid="topbar-search-mobile"
              className="h-9 pl-9 bg-muted/40 border-transparent text-sm"
              aria-label="Rechercher un lead"
            />
          </form>
        )}
      </div>
    </header>
  );
}
